// What a run's self-audit needs, loaded one way for both the Self-audit tab
// (RunAuditPanel.js) and the marks on the runs list, so the
// two can never count a run differently. The rules are lib/billingAudit.js.

import { supabase } from '../../../lib/supabase';
import { getVisitsByDateRange } from '../../../lib/axiscare';
import { authHistoryByClient, CLAIM_AUTH_TYPE } from '../../../lib/priorAuth';

const NONE = ['00000000-0000-0000-0000-000000000000'];

/** Clients, authorisation history and other runs' lines for a run. Returns { error } on failure. */
export const loadAuditBase = async (run, lines) => {
  const clientIds = [...new Set((lines || []).map((l) => l.client_id).filter(Boolean))];
  const ids = clientIds.length ? clientIds : NONE;
  const [clients, auths, others] = await Promise.all([
    supabase.from('clients')
      .select('id, client_id, first_name, last_name, current_status, is_test, superseded_by, dma_80_pa_number, pa_expiry_date, pa_skilled_hours_week, pa_pss_hours_week')
      .in('id', ids),
    supabase.from('prior_auth_history')
      .select('client_id, auth_type, auth_number, submitted_date, effective_date, expiry_date, skilled_hours_week, pss_hours_week, created_at')
      .eq('auth_type', CLAIM_AUTH_TYPE).in('client_id', ids),
    supabase.from('claim_lines')
      .select('billing_run_id, client_id, service_date, service_code, modifier, hours, source')
      .in('client_id', ids).neq('billing_run_id', run.id)
      .gte('service_date', run.date_from).lte('service_date', run.date_to),
  ]);
  // Manual claims carry no run, and .neq() drops nulls, so they come separately.
  const manual = await supabase.from('claim_lines')
    .select('billing_run_id, client_id, service_date, service_code, modifier, hours, source')
    .in('client_id', ids).is('billing_run_id', null)
    .gte('service_date', run.date_from).lte('service_date', run.date_to);
  const err = clients.error || auths.error || others.error || manual.error;
  if (err) return { error: err.message };
  return {
    clients: clients.data || [],
    authHistory: authHistoryByClient(auths.data || []),
    otherLines: [...(others.data || []), ...(manual.data || [])],
  };
};

// Caregivers live on the visits, not on claim lines, so the caregiver checks
// read the calendar for the run's week. Same source the run was built from.
/** The run week's AxisCare visits (raw response shape) and staff. Returns { error } on failure. */
export const loadAuditVisits = async (run) => {
  const [{ data, error }, st] = await Promise.all([
    getVisitsByDateRange(run.date_from, run.date_to, { mode: 'billing' }),
    supabase.from('staff').select('id, full_name, first_name, last_name, axiscare_id, position_code, exclusion_checked_at, license_verified_at'),
  ]);
  if (error) return { error: error.message || String(error) };
  return { visits: data?.results?.visits || [], staff: st.data || [] };
};
